import { ModalKind, ModalSize, ButtonKind } from "@spscommerce/ds-shared";
import clsx from "clsx";
import * as React from "react";

import * as PropTypes from "../prop-types";
import { selectChildren } from "../utils/selectChildren";
import { useElementId } from "../utils/useElementId";
import { SpsButton } from "../button/SpsButton";
import { I18nContext } from "../i18n";
import { PortalContext } from "../utils/usePortal";

const propsDoc = {
    kind: "ModalKind",
    size: "ModalSize",
    title: "string",
    onClose: "() => void",
};

const propTypes = {
    ...PropTypes.spsGlobalPropTypes,
    kind: PropTypes.enumValue<ModalKind>(ModalKind),
    size: PropTypes.enumValue<ModalSize>(ModalSize),
    title: PropTypes.string,
    onClose: PropTypes.fun<() => void>(),
};

export type SpsModalV2Props = PropTypes.InferTS<typeof propTypes, HTMLDivElement>;

export function SpsModalV2Footer({ children }) {
    return <div className="sps-modal__footer">{children}</div>;
}

Object.assign(SpsModalV2Footer, {
    props: {},
    propTypes: {},
    displayName: "SpsModalV2Footer"
});

export function SpsModalV2({
    children,
    className,
    "id": idProp,
    kind = ModalKind.INFO,
    size = ModalSize.SMALL,
    onClose,
    title,
    ...rest
}: SpsModalV2Props) {
    const id = useElementId(idProp);

    const classes = clsx(
        "sps-modal",
        "z-stratum-dialog",
        `sps-modal--${kind}`,
        `sps-modal--${size}`,
        className,
    );

    const { t } = React.useContext(I18nContext);

    const rootElement = React.useRef<HTMLDivElement>();

    React.useEffect(() => {
        if (rootElement.current) {
            rootElement.current.focus();
        }
        document.body.style.overflow = "hidden";
        return () => document.body.style.overflow = "auto";
    }, []);

    const handleKeydown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event && event.key === "Escape" && onClose) {
            onClose();
        }
    }, [onClose]);

    const [
        [footer],
        bodyContent
    ] = selectChildren(children, [{ type: SpsModalV2Footer }]);

    return <div id={id.current}
        role="dialog"
        aria-modal={true}
        aria-labelledby={`${id.current}-title`}
        className={classes}
        onKeyDown={handleKeydown}
        tabIndex={-1}
        ref={rootElement}
    >
        <div {...rest} className="sps-modal__dialog" onClick={event => event.stopPropagation()}>
            <div className="sps-modal__titlebar">
                <div className="sps-modal__title" id={`${id.current}-title`}>{title || t(`design-system:modal.defaultTitle.${kind}`)}</div>
                {onClose && <div className="sps-button sps-button--icon" onClick={onClose}>
                    <button type="button"><i className="sps-icon sps-icon-x"/></button>
                </div>}
            </div>
            <div className="sps-modal__body">
                <PortalContext.Provider value={{ parentElementRef: rootElement, fixed: true }}>
                    {bodyContent}
                </PortalContext.Provider>
            </div>
            {footer || <div className="sps-modal__footer">
                <SpsButton kind={kind === ModalKind.SERIOUS_WARNING ? ButtonKind.DELETE : ButtonKind.KEY}
                    onClick={onClose}>
                    {t("design-system:modal.defaultButtonLabel")}
                </SpsButton>
            </div>}
        </div>
    </div>;
}

Object.assign(SpsModalV2, {
    props: propsDoc,
    propTypes,
    displayName: "SpsModalV2"
});
